import {DragDrop} from '@uppy/react'
import Uppy from '@uppy/core'
import XHRUpload from '@uppy/xhr-upload'
import '@uppy/core/dist/style.min.css'
import '@uppy/drag-drop/dist/style.min.css'
import {AuthModel, getAuth} from '../../auth'
import {useEffect, useMemo, useState} from 'react'
import {BasicButton} from '../../widgets/components/UI/BasicButton'
import {useIntl} from 'react-intl'
import {toAbsoluteUrl} from '../../../../_metronic/helpers'
import {toast} from 'react-toastify'

const {token} = (getAuth() as AuthModel) || {}
if (!token) {
  getAuth()
}

export const DragDropFile = ({
  onFileUploadCompleted,
  fileUploaded,
  index,
}: {
  fileUploaded: object[]
  onFileUploadCompleted?: (id: number, name: string, index: number) => void
  index: number
}) => {
  const {formatMessage} = useIntl()
  const [isUploading, setIsUploading] = useState(false)
  const [files, setFiles] = useState<{id: string; name: string}[]>([])
  const uppy = useMemo(() => {
    return new Uppy({
      id: `fileUploadUppy+${index}`,
      autoProceed: false,
      debug: false,
      restrictions: {
        maxFileSize: 10485760,
        maxNumberOfFiles: 10,
        allowedFileTypes: [
          'image/*',
          '.jpg',
          '.jpeg',
          '.png',
          'application/pdf',
          'application/msword',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        ],
      },
      meta: {module: 'doc+img', isProtected: true},
    }).use(XHRUpload, {
      id: 'fileDragDrop',
      endpoint: `${process.env.REACT_APP_BASE_API_URL}/file`,
      fieldName: 'file',
      formData: true,
      bundle: false,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const handleFileAdded = (file: any) => {
      setFiles((prevFiles) => [...prevFiles, {id: file.id, name: file.name}])
    }
    uppy.on('file-added', handleFileAdded)
    return () => {
      uppy.off('file-added', handleFileAdded)
    }
  }, [uppy])

  const handleUploadClick = () => {
    setIsUploading(true)
    uppy.upload().then((result: any) => {
      if (result.successful) {
        setIsUploading(false)
        result.successful.forEach((file: any) => {
          if (onFileUploadCompleted) {
            onFileUploadCompleted(
              file.response?.body.data.fileId,
              file.response?.body.data.name,
              index
            )
          }
          uppy.removeFile(file.id)
        })
        setFiles([])
      } else {
        setIsUploading(false)
        result.failed.forEach((file: any) => {
          toast.error(formatMessage({id: `Upload failed for file: ${file.name}`}))
        })
      }
    })
  }
  const handleRemoveClick = (fileId: string) => {
    uppy.removeFile(fileId)
    setFiles((prevFiles) => prevFiles.filter((file) => file.id !== fileId))
  }

  useEffect(() => {
    return () => uppy.clear()
  }, [uppy])

  const formatFileName = (input: string) => {
    let output = input?.replace(/-/g, ' ')
    output = output?.replace(/\d{13}/g, '')
    return output?.trim()
  }

  return (
    <>
      <DragDrop uppy={uppy} width='100%' height='100%'/>
      <BasicButton
        buttonText={formatMessage({id: 'Upload'})}
        height='36px'
        border='none'
        color='#4776E6'
        textColor='#FFFFFF'
        padding='8px 24px'
        disabled={isUploading}
        hidden={uppy.getFiles().length === 0 || fileUploaded.length + files.length > 10}
        onClick={handleUploadClick}
        loading={isUploading}
      />
      <div className='d-flex flex-wrap gap-5'>
        {files?.map((file) => {
          return (
            <div
              key={file.id}
              className='d-flex justify-content-between align-items-center gap-3 border rounded p-1 px-2 background-color-drag'
            >
              <p className='m-0'>{file.name}</p>
              <img
                onClick={() => handleRemoveClick(file.id)}
                src={toAbsoluteUrl('/media/icons/investor-updates/deleteRed.svg')}
                alt='garbage icon'
                className='cursor-pointer'
              />
            </div>
          )
        })}
        {fileUploaded?.map((file: any) => {
          return (
            <div
              key={file.id}
              className='d-flex justify-content-between align-items-center gap-3 border rounded p-1 px-2 background-color-drag'
            >
              <p className='m-0'>{formatFileName(file.name)}</p>
              <img
                src={toAbsoluteUrl('/media/icons/investor-updates/tickIcon_green.svg')}
                alt='greenTick icon'
              />
            </div>
          )
        })}
      </div>
    </>
  )
}
